import { LinePlus } from '@qr-front/icons/line-plus'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { Checkbox } from '@qr-front/ui/components/Checkbox'
import { cn } from '@qr-front/ui/utils'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'

import { InputButton } from '../InputButton'
import {
	defaultRangeEndTime,
	defaultRangeStartTime,
	openingHoursStateAtom,
	sharedOpeningHoursStateAtom,
	weekDays,
	weekDaysAreSharedAtom,
} from './openingHoursState'
import { TTimeRange } from './openingHoursTypes'
import { selectedTabAtom } from './TabNavigation'
import { TimePicker } from './TimePicker'

export const SharedWeekDays = () => {
	const t = useTranslations()
	const selectedTab = useAtomValue(selectedTabAtom)

	const [weekDaysAreShared, setWeekDaysAreShared] = useAtom(
		weekDaysAreSharedAtom,
	)
	const setOpeningHoursData = useSetAtom(openingHoursStateAtom)
	const [sharedOpeningHoursData, setSharedOpeningHoursData] = useAtom(
		sharedOpeningHoursStateAtom,
	)
	const sharedWeekdayRanges = sharedOpeningHoursData?.monday?.dateRanges ?? [
		{ start: defaultRangeStartTime, end: defaultRangeEndTime },
	]

	const updateSharedOpeningHours = (newRanges: TTimeRange[]) => {
		const updatedShared = { ...sharedOpeningHoursData }

		weekDays.slice(0, 5).forEach((day) => {
			updatedShared[day] = {
				...updatedShared[day],
				isActive: true,
				dateRanges: [...newRanges],
			}
		})

		setSharedOpeningHoursData(updatedShared)
	}

	const syncSharedOpeningHoursState = (ranges: TTimeRange[]) => {
		if (!weekDaysAreShared) return

		setSharedOpeningHoursData((prev) => {
			const updated = { ...prev }
			weekDays.slice(0, 5).forEach((day) => {
				updated[day] = {
					...updated[day],
					isActive: true,
					dateRanges: [...ranges],
				}
			})
			return updated
		})
	}

	const handleRangeChange = (
		index: number,
		field: 'start' | 'end',
		value: string,
	) => {
		const updatedRanges = [...sharedWeekdayRanges]
		if (!updatedRanges[index]) return

		updatedRanges[index] = {
			...updatedRanges[index],
			[field]: value,
		}

		updateSharedOpeningHours(updatedRanges)
		syncSharedOpeningHoursState(updatedRanges)
	}

	const handleAddTimeRange = () => {
		if (sharedWeekdayRanges.length >= 2) return

		const newRanges = [
			...sharedWeekdayRanges,
			{ start: defaultRangeStartTime, end: defaultRangeEndTime },
		]

		updateSharedOpeningHours(newRanges)
		syncSharedOpeningHoursState(newRanges)
	}

	const handleRemoveTimeRange = (index: number) => {
		if (sharedWeekdayRanges.length <= 1) return

		const newRanges = sharedWeekdayRanges.filter((_, i) => i !== index)
		updateSharedOpeningHours(newRanges)
		syncSharedOpeningHoursState(newRanges)
	}

	return (
		<div className="rounded-10 bg-light-grey-70 p-4">
			<div
				className={cn(
					'flex flex-col gap-4 tablet:flex-row tablet:justify-between',
					!weekDaysAreShared && 'opacity-50',
				)}
			>
				<div className="flex gap-2">
					<Checkbox
						id="monday-friday"
						checked={weekDaysAreShared}
						onCheckedChange={(checked) => {
							const isChecked = Boolean(checked)
							setWeekDaysAreShared(isChecked)

							setSharedOpeningHoursData((prev) => {
								const updated = { ...prev }
								if (isChecked) {
									weekDays.slice(0, 5).forEach((day) => {
										if (updated[day]) {
											updated[day] = {
												...updated[day],
												isActive: true,
												dateRanges: [...sharedWeekdayRanges],
											}
										}
									})
								} else {
									weekDays.slice(0, 5).forEach((day) => {
										if (updated[day]) {
											updated[day] = {
												...updated[day],
												isActive: false,
											}
										}
									})
								}
								return updated
							})

							setOpeningHoursData((prev) => {
								const updated = { ...prev }
								if (isChecked) {
									weekDays.slice(0, 5).forEach((day) => {
										if (updated[day]) {
											updated[day] = {
												...updated[day],
												isActive: true,
											}
										}
									})
								} else {
									weekDays.slice(0, 5).forEach((day) => {
										if (updated[day]) {
											updated[day] = {
												...updated[day],
												isActive: false,
											}
										}
									})
								}
								return updated
							})
						}}
						label={t(
							'generator__content_form_section__opening__hours_section__daynames__monday_friday',
						)}
						className="type-body-medium mt-2 items-start"
					/>
				</div>

				<div className="flex flex-col gap-2">
					<div className="flex gap-2">
						<TimePicker
							value={sharedWeekdayRanges[0]?.start || defaultRangeStartTime}
							selectedTab={selectedTab}
							onChange={(newStart) => handleRangeChange(0, 'start', newStart)}
							disabled={!weekDaysAreShared}
						/>

						<TimePicker
							value={sharedWeekdayRanges[0]?.end || defaultRangeEndTime}
							selectedTab={selectedTab}
							onChange={(newEnd) => handleRangeChange(0, 'end', newEnd)}
							disabled={!weekDaysAreShared}
						/>

						<InputButton
							icon={<LinePlus className="size-4" />}
							onClick={handleAddTimeRange}
							size="icon"
							className={cn(
								!weekDaysAreShared || sharedWeekdayRanges.length >= 2
									? 'pointer-events-none text-grey'
									: 'text-black',
								weekDaysAreShared && sharedWeekdayRanges.length >= 2
									? 'pointer-events-none opacity-50'
									: '',
							)}
						/>
					</div>

					{sharedWeekdayRanges.length === 2 && (
						<div className="flex gap-2">
							<TimePicker
								value={sharedWeekdayRanges[1]?.start || defaultRangeStartTime}
								selectedTab={selectedTab}
								onChange={(newStart) => handleRangeChange(1, 'start', newStart)}
								disabled={!weekDaysAreShared}
							/>

							<TimePicker
								value={sharedWeekdayRanges[1]?.end || defaultRangeEndTime}
								selectedTab={selectedTab}
								onChange={(newEnd) => handleRangeChange(1, 'end', newEnd)}
								disabled={!weekDaysAreShared}
							/>

							<InputButton
								icon={
									<LineTrashAlt
										className={cn(
											'size-4',
											weekDaysAreShared
												? 'text-black'
												: 'pointer-events-none text-grey',
										)}
									/>
								}
								onClick={() => handleRemoveTimeRange(1)}
								size="icon"
								className={cn(
									weekDaysAreShared && sharedWeekdayRanges.length === 2
										? ''
										: 'pointer-events-none',
								)}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
